import { useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { usePropertyAssessmentsQuery } from '@/hooks/queries/usePropertyDashboard';
import type { Assessment } from '@/hooks/queries/usePropertyDashboard';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import {
  DataTable,
  TableColumn,
  TableAction,
  PaginationInfo,
} from '@/components/shared/data-table';
import { Eye } from 'lucide-react';
import { cn } from '@/lib/utils';

interface AssessmentsTabProps {
  propertyId?: string;
}

export function AssessmentsTab({ propertyId }: AssessmentsTabProps) {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 10;

  const isRTL = useMemo(() => i18n.language === 'ar', [i18n.language]);

  // Use React Query hook
  const { data: response, isLoading } = usePropertyAssessmentsQuery(
    propertyId || '',
    currentPage,
    itemsPerPage
  );

  const assessments = response?.data || [];
  const pagination = response?.pagination;

  const handlePageChange = (page: number) => {
    if (pagination) {
      setCurrentPage(Math.max(1, Math.min(page, pagination.last_page)));
    }
  };

  const handleViewDetails = (assessment: Assessment) => {
    navigate(`/assessments/perform/${assessment.id}`);
  };

  const handleMidStandardClick = (assessment: Assessment) => {
    navigate(`/properties/${propertyId}/mid-standard/${assessment.id}`);
  };

  // Define table columns
  const columns: TableColumn<Assessment>[] = [
    {
      key: 'date',
      label: t('properties.details.assessmentsTab.date'),
      render: assessment => new Date(assessment.date).toLocaleDateString(),
    },
    {
      key: 'auditor',
      label: t('properties.details.assessmentsTab.inspector'),
    },
    {
      key: 'score',
      label: t('properties.details.assessmentsTab.score'),
      render: assessment => (
        <div
          className={cn(
            'flex items-center space-x-2',
            isRTL && 'flex-row-reverse space-x-reverse'
          )}
        >
          <span className="font-medium">{assessment.score}%</span>
          <div className="w-16 bg-muted rounded-full h-2">
            <div
              className="bg-primary h-2 rounded-full"
              style={{ width: `${assessment.score}%` }}
            />
          </div>
        </div>
      ),
    },
    {
      key: 'mid_standard_percentage',
      label: t('properties.details.assessmentsTab.midStandard'),
      render: assessment => (
        <Button
          variant="ghost"
          size="sm"
          className="text-red-600 hover:text-red-700 hover:bg-red-50"
          onClick={() => handleMidStandardClick(assessment)}
        >
          {assessment.mid_standard_percentage}%
        </Button>
      ),
    },
    {
      key: 'status',
      label: t('properties.details.assessmentsTab.status'),
      render: assessment => {
        switch (assessment.status) {
          case 'completed':
            return (
              <Badge variant="secondary">
                {t('properties.details.assessmentsTab.completed')}
              </Badge>
            );
          case 'in_progress':
            return (
              <Badge variant="default">
                {t('properties.details.assessmentsTab.inProgress')}
              </Badge>
            );
          default:
            return <Badge variant="secondary">{assessment.status}</Badge>;
        }
      },
    },
  ];

  // Define table actions
  const actions: TableAction<Assessment>[] = [
    {
      label: t('properties.details.assessmentsTab.viewDetails'),
      icon: Eye,
      onClick: handleViewDetails,
    },
  ];

  // Pagination info
  const paginationInfo: PaginationInfo = {
    current: pagination?.current_page || 1,
    total: pagination?.last_page || 1,
    count: pagination?.total || 0,
    itemsPerPage: pagination?.per_page || itemsPerPage,
  };

  return (
    <div className="space-y-4">
      {/* Assessments Table */}
      <Card>
        <CardHeader>
          <div
            className={cn(
              'flex items-center justify-between',
              isRTL && 'flex-row-reverse'
            )}
          >
            <div className={cn(isRTL && 'text-right')}>
              <CardTitle>
                {t('properties.details.assessmentsTab.recentAssessments')}
              </CardTitle>
              <CardDescription>
                {t('properties.details.assessmentsTab.recentAssessmentsDesc')}
              </CardDescription>
            </div>
            <div
              className={cn(
                'text-sm text-muted-foreground',
                isRTL && 'text-right'
              )}
            >
              {isLoading
                ? t('loading')
                : pagination
                  ? t('properties.details.assessmentsTab.showingAssessments', {
                      start: pagination.from || 0,
                      end: pagination.to || 0,
                      total: pagination.total,
                    })
                  : null}
            </div>
          </div>
        </CardHeader>
        <CardContent className="p-0">
          {isLoading ? (
            <div className="flex items-center justify-center py-12">
              <div className="text-muted-foreground">{t('loading')}</div>
            </div>
          ) : (
            <DataTable
              data={assessments}
              columns={columns}
              actions={actions}
              pagination={paginationInfo}
              onPageChange={handlePageChange}
              emptyMessage={t(
                'properties.details.assessmentsTab.noAssessments'
              )}
              tableCaption={t(
                'properties.details.assessmentsTab.recentAssessments'
              )}
            />
          )}
        </CardContent>
      </Card>
    </div>
  );
}
